import { useMemo } from 'react';
import { useClientsQuery } from '@/features/Clients/Data';
import { useEstadiasDoEquipamentoQuery } from '../../../hooks/data';
import type { EstadiaCliente, Movimentacao } from '../../../models';
import {
  PROCESSO_MOVIMENTACAO_LABELS,
  STATUS_ESTADIA_CLIENTE_LABELS,
  STATUS_ESTADIA_CLIENTE_BADGE_CLASS,
  METODO_ENTREGA_LABELS,
  TIPO_CHAMADO_CLIENTE_LABELS,
} from '../../EquipamentosListPage/equipamentosListPage.definitions';

interface HistoricoEstadiasEquipamentoSectionProps {
  equipamentoId: string;
}

function formatarData(timestamp: Movimentacao['dataEnvio']): string | null {
  if (!timestamp) return null;
  const data = 'toDate' in timestamp ? timestamp.toDate() : new Date(timestamp as unknown as string);
  return data.toLocaleDateString('pt-BR');
}

/**
 * Resumo só-leitura do histórico de passagens desse Equipamento por Clientes que exigem
 * máquina homologada (ver estadiaCliente.types.ts) — mostrado na Movimentação pra dar
 * visibilidade rápida de quando foi pro cliente, forma/código, e status, sem precisar ir
 * até Equipamentos → Estadias em Cliente. Nenhuma ação aqui, só leitura — edição continua
 * exclusiva de EditarEstadiaModal/EstadiaClienteCard.
 */
export default function HistoricoEstadiasEquipamentoSection({ equipamentoId }: HistoricoEstadiasEquipamentoSectionProps) {
  const { data: estadias = [] } = useEstadiasDoEquipamentoQuery(equipamentoId);
  const { data: clientes = [] } = useClientsQuery();

  const nomeClientePorId = useMemo(() => new Map(clientes.map((cliente) => [cliente.id, cliente.name])), [clientes]);

  if (estadias.length === 0) return null;

  return (
    <details className="mt-4 border-t border-gray-100 pt-4">
      <summary className="cursor-pointer text-sm font-semibold text-primary-700">
        Histórico com cliente ({estadias.length})
      </summary>
      <div className="mt-2 space-y-2">
        {estadias.map((estadia: EstadiaCliente) => (
          <div key={estadia.id} className="rounded border border-gray-100 bg-gray-50 p-2 text-sm">
            <div className="flex items-center justify-between gap-2">
              <p className="font-medium text-gray-900">
                {nomeClientePorId.get(estadia.clienteId) ?? '—'} · {PROCESSO_MOVIMENTACAO_LABELS[estadia.motivo]}
              </p>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_ESTADIA_CLIENTE_BADGE_CLASS[estadia.status]}`}
              >
                {STATUS_ESTADIA_CLIENTE_LABELS[estadia.status]}
              </span>
            </div>
            {estadia.formaEnvio && (
              <p className="mt-1 text-gray-600">
                <span className="font-semibold text-gray-900">Envio:</span> {METODO_ENTREGA_LABELS[estadia.formaEnvio]}
                {estadia.codigoRastreio ? ` · ${estadia.codigoRastreio}` : ''}
              </p>
            )}
            {(formatarData(estadia.dataEnvioRaro) || formatarData(estadia.dataChegadaCliente)) && (
              <p className="mt-1 text-gray-600">
                <span className="font-semibold text-gray-900">Enviada em:</span>{' '}
                {formatarData(estadia.dataEnvioRaro) ?? 'a definir'} ·{' '}
                <span className="font-semibold text-gray-900">Recebida em:</span>{' '}
                {formatarData(estadia.dataChegadaCliente) ?? 'a definir'}
              </p>
            )}
            {estadia.motivoEncerramento === 'retornado_para_raro' && estadia.formaRecolhimento && (
              <p className="mt-1 text-gray-600">
                <span className="font-semibold text-gray-900">Recolhimento:</span>{' '}
                {METODO_ENTREGA_LABELS[estadia.formaRecolhimento]}
                {estadia.codigoRastreioRecolhimento ? ` · ${estadia.codigoRastreioRecolhimento}` : ''}
                {formatarData(estadia.dataRecolhimento) ? ` · ${formatarData(estadia.dataRecolhimento)}` : ''}
              </p>
            )}
            {estadia.chamados.length > 0 && (
              <div className="mt-1 space-y-0.5">
                {estadia.chamados.map((chamado) => (
                  <p key={chamado.id} className="text-gray-600">
                    <span className="font-semibold text-gray-900">Chamado:</span> {TIPO_CHAMADO_CLIENTE_LABELS[chamado.tipo]}
                    {chamado.numero ? ` — ${chamado.numero}` : ''}
                  </p>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </details>
  );
}
